import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { intersection, isEqual } from "lodash";
import moment from "moment";
import PropTypes from "prop-types";
import queryString from "query-string";
import {
  ADD_CALENDAR_EVENT_SUCCESS,
  EDIT_CALENDAR_EVENT_SUCCESS,
  RESCHEDULE_EXCEPTION_CALENDAR_EVENT_SUCCESS,
  RESCHEDULE_THIS_AND_FUTURE_CALENDAR_EVENTS_SUCCESS,
} from "~/store/action_types";
import { addCalendarEvent } from "~/store/actions/actions.calendarEvents";
import { updateSubscribers } from "~/store/actions/actions.subscription";
import { getCalendarEventById } from "~/store/selectors/selector.calendarEvents";
import { getInternalCalendars } from "~/store/selectors/selector.calendars";
import { getUserTimezone } from "~/store/selectors/selector.configOptions";
import { getInstanceId } from "~/store/selectors/selector.instance";
import { getPossibleSubscribers, getSubscriberIdsByTypeAndId } from "~/store/selectors/selector.subscription";
import { getLoggedUser } from "~/store/selectors/selector.users";
import CalendarEventForm from "./CalendarEventForm";
import { useCalendarEventRescheduleContext } from "./CalendarEventRescheduleContext";
import { isRepeating, makeEventForDate } from "../Calendar/calendarUtils";
import { useDraft } from "../Hooks/useDraft";
import { showErrorToast } from "../Toast";

const CalendarEventFormContainer = ({
  calendarEventId,
  onClose,
  className,
  startsOnTime,
  startsOn,
  endsOnTime,
  endsOn,
  occurrence,
}) => {
  const [draft, setDraft] = useDraft("calendar_event");
  const instanceId = useSelector(getInstanceId);
  const timezone = useSelector(getUserTimezone);
  const loggedUser = useSelector(getLoggedUser);
  const loggedUserId = loggedUser.id;
  const _event = useSelector((state) => getCalendarEventById(state, calendarEventId));
  const { rescheduleEvent } = useCalendarEventRescheduleContext();

  const { search } = useLocation();
  const params = queryString.parse(search);
  const event = useMemo(() => {
    if (_event) {
      if (occurrence) {
        return makeEventForDate(_event, timezone, occurrence);
      }
      return makeEventForDate(_event, timezone, params?.date);
    }
    return undefined;
  }, [_event, occurrence, params?.date, timezone]);

  const draftId = calendarEventId
    ? `instance_${instanceId}_user_${loggedUserId}_calendar_event_${event?.id}`
    : `instance_${instanceId}_user_${loggedUserId}_calendar_event`;
  const hasDraft = !!(draft && draft[draftId]);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const subscribers = useSelector((state) => getSubscriberIdsByTypeAndId(state, "CalendarEvent", calendarEventId));
  const activeCalendars = useSelector(getInternalCalendars);

  const selectedCalendar = useMemo(() => {
    if (hasDraft && draft[draftId]?.calendar_id) {
      return activeCalendars.find((i) => i.id === draft[draftId].calendar_id);
    }

    if (event && event?.calendar_id) {
      return activeCalendars.find((i) => i.id === event.calendar_id);
    }

    return [...activeCalendars].sort((a, b) => a.name.localeCompare(b.name))[0];
  }, [activeCalendars, hasDraft, draft, draftId, event]);

  const members = useMemo(() => (selectedCalendar ? selectedCalendar?.members : []), [selectedCalendar]);

  const defaultVisibleOnWorkload = useMemo(
    () => (selectedCalendar ? selectedCalendar?.is_visible_on_workload : false),
    [selectedCalendar]
  );

  const possibleSubscribers = useSelector((state) => getPossibleSubscribers(state, members));

  const userIds = possibleSubscribers.map((s) => s.id);

  const { t } = useTranslation();

  const submitButtonLabel = calendarEventId > 0 ? t("Save") : t("Add Event");

  const handleClose = useCallback(() => {
    const promise = new Promise((resolve) => {
      setDraft((prevState) => {
        if (!prevState) return undefined;
        const { [draftId]: current, ...rest } = prevState;
        return rest;
      });
      setTimeout(() => {
        resolve(true);
      }, 100);
    });
    promise.then(() => {
      if (typeof onClose === "function") {
        onClose();
      }
    });
  }, [draftId, onClose, setDraft]);

  const handleRemoveDraft = useCallback(() => {
    const { [draftId]: current, ...rest } = draft;
    setDraft(rest);
  }, [draftId, setDraft, draft]);

  const handleSubmit = useCallback(
    async (attributes) => {
      const isAllDay = attributes?.starts_on_time === null && attributes?.ends_on_time === null;
      handleRemoveDraft();
      if (event && calendarEventId) {
        const first = [...attributes.subscribers].sort();
        const second = [...subscribers].sort();
        if (!isEqual(first, second)) {
          await dispatch(updateSubscribers(calendarEventId, "calendar-event", attributes.subscribers));
        }

        const action = await rescheduleEvent({
          ...attributes,
          reference_timezone: isAllDay ? "UTC" : timezone,
        });

        if (action?.type) {
          if (
            action.type === EDIT_CALENDAR_EVENT_SUCCESS ||
            action.type === RESCHEDULE_EXCEPTION_CALENDAR_EVENT_SUCCESS
          ) {
            navigate(`?modal=CalendarEvent-${calendarEventId}-${attributes?.calendar_id}`);
            handleClose();
          } else {
            if (action.type === RESCHEDULE_THIS_AND_FUTURE_CALENDAR_EVENTS_SUCCESS) {
              navigate(`?modal=CalendarEvent-${action.response.result.single}-${attributes?.calendar_id}`);
              handleClose();
            } else {
              showErrorToast(t("Unable to update event."));
            }
          }
        } else {
          return action;
        }
      } else {
        const action = await dispatch(
          addCalendarEvent(attributes.calendar_id, {
            ...attributes,
            reference_timezone: isAllDay ? "UTC" : timezone,
          })
        );
        if (action.type === ADD_CALENDAR_EVENT_SUCCESS) {
          navigate(`?modal=CalendarEvent-${action.response.result.single}-${action?.calendarId}`);
          handleClose();
        } else {
          showErrorToast(t("Unable to create event"));
        }
      }
    },
    [
      handleRemoveDraft,
      event,
      calendarEventId,
      subscribers,
      rescheduleEvent,
      dispatch,
      navigate,
      handleClose,
      t,
      timezone,
    ]
  );

  const handleSaveDraft = useCallback(
    (attributes) => {
      const { starts_on, ends_on, starts_on_time, ends_on_time, repeat_until, ...rest } = attributes;

      if (calendarEventId) {
        setDraft((prevState) => {
          if (isRepeating(attributes)) {
            return {
              ...prevState,
              [draftId]: rest,
            };
          }
          return {
            ...prevState,
            [draftId]: attributes,
          };
        });
      } else {
        setDraft((prevState) => {
          return {
            ...prevState,
            [draftId]: rest,
          };
        });
      }
    },
    [calendarEventId, draftId, setDraft]
  );

  const formData = useMemo(() => {
    let object = {
      calendar_event_id: calendarEventId,
      name: event?.name || "",
      meeting_url: event?.meeting_url || "",
      calendar_id: event?.calendar_id || selectedCalendar?.id,
      starts_on: event
        ? event?.starts_on_time === null
          ? moment.unix(event.starts_on).utc().format("YYYY-MM-DD")
          : moment.unix(event.starts_on).format("YYYY-MM-DD")
        : startsOn,
      ends_on: event
        ? event?.ends_on_time === null
          ? moment.unix(event.ends_on).utc().format("YYYY-MM-DD")
          : moment.unix(event.ends_on).format("YYYY-MM-DD")
        : endsOn,
      starts_on_time: event?.starts_on_time ? moment.unix(event?.starts_on).format("HH:mm:ss") : startsOnTime,
      ends_on_time: event?.ends_on_time ? moment.unix(event?.ends_on).format("HH:mm:ss") : endsOnTime,
      repeat_event: event?.repeat_event || "dont",
      repeat_until: event?.repeat_until ? moment.unix(event.repeat_until).format("YYYY-MM-DD") : null,
      recurrence_instance: event?.recurrence_instance ? event.recurrence_instance : null,
      body: event ? event.body_formatted : "",
      attachments: event ? event.attachments : [],
      subscribers: event ? subscribers : [loggedUser.id],
      participants: event ? event.participants : [loggedUser.email],
      attach_uploaded_files: [],
      drop_attached_files: [],
      submitButtonLabel,
      user_ids: userIds,
      is_visible_on_workload: event ? event?.is_visible_on_workload : defaultVisibleOnWorkload,
    };
    if (hasDraft) {
      let subscribersIds = intersection(userIds, draft[draftId].subscribers);
      object = {
        ...object,
        ...draft[draftId],
        id: calendarEventId,
        subscribers: subscribersIds,
      };
    }
    return object;
  }, [
    calendarEventId,
    event,
    selectedCalendar?.id,
    startsOn,
    endsOn,
    startsOnTime,
    endsOnTime,
    subscribers,
    loggedUser.id,
    loggedUser.email,
    submitButtonLabel,
    userIds,
    defaultVisibleOnWorkload,
    hasDraft,
    draft,
    draftId,
  ]);

  return (
    <CalendarEventForm
      calendarEventId={formData.calendar_event_id}
      name={formData.name}
      meetingUrl={formData.meeting_url}
      calendarId={formData.calendar_id}
      endsOn={formData.ends_on}
      startsOn={formData.starts_on}
      startsOnTime={formData.starts_on_time}
      endsOnTime={formData.ends_on_time}
      repeatEvent={formData.repeat_event}
      repeatUntil={formData.repeat_until}
      recurrence_instance={formData.recurrence_instance}
      description={formData.body}
      attachments={formData.attachments}
      dropAttachedFiles={formData.drop_attached_files}
      attachUploadedFiles={formData.attach_uploaded_files}
      subscribers={formData.subscribers}
      participants={formData.participants}
      visibleOnWorkload={formData.is_visible_on_workload}
      submitButtonLabel={formData.submitButtonLabel}
      userIds={formData.user_ids}
      onCancel={handleClose}
      onSubmit={handleSubmit}
      onChange={handleSaveDraft}
      className={className}
      hasDraft={hasDraft}
    />
  );
};

CalendarEventFormContainer.propTypes = {
  calendarEventId: PropTypes.number,
  onClose: PropTypes.func,
  className: PropTypes.string,
  startsOn: PropTypes.string,
  endsOn: PropTypes.string,
  startsOnTime: PropTypes.string,
  endsOnTime: PropTypes.string,
  enabledWorkload: PropTypes.bool,
  featureEventOnWorkload: PropTypes.bool,
  occurrence: PropTypes.number,
};

CalendarEventFormContainer.defaultProps = {
  calendarEventId: 0,
  startsOn: moment().format("YYYY-MM-DD"),
  endsOn: moment().format("YYYY-MM-DD"),
  startsOnTime: null,
  endsOnTime: null,
};

export default CalendarEventFormContainer;
